import Phaser from 'phaser';
import { COLORS } from '../main';
import type { Die } from '../types';
import { describeCost } from '../game/cost';
import { GameScene } from './GameScene';
import { makeDieSprite } from '../ui/diceRender';

export interface CardZoomData {
  cardId: string;
  tieBreakerDice?: Die[];
  onChoose?: (dieId: string) => void;
}

export class CardZoomScene extends Phaser.Scene {
  data2!: CardZoomData;

  constructor() {
    super('CardZoom');
  }

  init(data: CardZoomData): void {
    this.data2 = data;
  }

  create(): void {
    const game = this.scene.get('Game') as GameScene;
    const card = game.controller.state.pyramid.find((c) => c.id === this.data2.cardId);
    if (!card) {
      this.closeAndResume();
      return;
    }

    const { width, height } = this.scale;

    const overlay = this.add
      .rectangle(0, 0, width, height, 0x000000, 0.7)
      .setOrigin(0)
      .setInteractive();
    overlay.on('pointerdown', () => {
      if (!this.data2.tieBreakerDice) this.closeAndResume();
    });

    const panelW = width * 0.85;
    const panelH = height * 0.6;
    const panelY = height / 2 - panelH / 2;
    this.add
      .rectangle(width / 2, height / 2, panelW, panelH, COLORS.card)
      .setStrokeStyle(2, COLORS.accent);

    this.add
      .text(width / 2, panelY + 16, `Row ${card.row + 1}, slot ${card.col + 1}`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#9ca3af',
      })
      .setOrigin(0.5, 0);

    this.add
      .text(width / 2, panelY + 50, describeCost(card.cost), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '24px',
        fontStyle: 'bold',
        color: '#e6edf3',
        align: 'center',
        wordWrap: { width: panelW - 40 },
      })
      .setOrigin(0.5, 0);

    const rewardLines =
      card.rewards.length === 0
        ? '(no rewards — winning the apex is the prize)'
        : card.rewards
            .map((r) => {
              if (r.kind === 'addDie')
                return `+1 ${r.die.color} d${r.die.faces}`;
              return r.token === 'reroll'
                ? '+1 reroll token'
                : '+1 convert-color token';
            })
            .join('\n');

    this.add
      .text(width / 2, panelY + 110, `Rewards:\n${rewardLines}`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: '#fbbf24',
        align: 'center',
        lineSpacing: 4,
      })
      .setOrigin(0.5, 0);

    // Show placed dice
    if (card.placedDice.length > 0) {
      const placedY = panelY + panelH - 110;
      this.add
        .text(width / 2, placedY - 22, 'Placed:', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: '#9ca3af',
        })
        .setOrigin(0.5);
      const size = 38;
      const totalW = card.placedDice.length * size + (card.placedDice.length - 1) * 6;
      let x = width / 2 - totalW / 2 + size / 2;
      for (const d of card.placedDice) {
        const dg = makeDieSprite(this, x, placedY, size, d);
        this.add.existing(dg.container);
        x += size + 6;
      }
    }

    // Tie-breaker prompt mode
    if (this.data2.tieBreakerDice && this.data2.tieBreakerDice.length > 0) {
      this.add
        .text(width / 2, panelY + panelH - 60, 'Choose which tied die to lock:', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: '#fbbf24',
        })
        .setOrigin(0.5);
      const size = 50;
      const totalW =
        this.data2.tieBreakerDice.length * size +
        (this.data2.tieBreakerDice.length - 1) * 8;
      let x = width / 2 - totalW / 2 + size / 2;
      const y = panelY + panelH - 24;
      for (const d of this.data2.tieBreakerDice) {
        const dg = makeDieSprite(this, x, y, size, d);
        dg.bg.setInteractive({ useHandCursor: true });
        dg.bg.on('pointerdown', () => {
          this.data2.onChoose?.(d.id);
          this.scene.stop();
        });
        this.add.existing(dg.container);
        x += size + 8;
      }
    } else {
      this.add
        .text(width / 2, panelY + panelH - 24, 'tap anywhere to close', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color: '#6b7280',
        })
        .setOrigin(0.5);
    }
  }

  private closeAndResume(): void {
    this.scene.resume('Game');
    this.scene.stop();
  }
}
